import { Box, Stack, Typography } from '@mui/material';
import {
  ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';

import { AccordionContext } from '@/components/Accordion/Accordion.context';
import { Button } from '@/components/form/baseInputs/Button';
import { LinkButton } from '@/components/form/baseInputs/Link/LinkButton';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { AlertTriangleFilledIcon } from '@/components/icons/AlertTriangleFilledIcon';
import { CheckFilledIcon } from '@/components/icons/CheckFilledIcon';
import { Home03Icon } from '@/components/icons/Home03Icon';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { SortableList } from '@/components/lists/DragAndDropList/SortableList';
import { useFormContext } from '@/components/react-hook-form';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import { defaultRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import { DispositiveProvisionsFormRecipient } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import { calculateResiduaryRecipients } from '@/modules/dispositiveProvisions/DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/primaryClientDeathSection.utils';
import { EntityType } from '@/modules/entities/types/EntityType';
import { COLORS } from '@/styles/tokens/colors';
import {
  DispositiveProvisionDispositionKind,
  TransferMethod,
} from '@/types/schema';
import { PathsOf } from '@/types/subform';

import { BeneficiaryDesignationRecipient } from './BeneficiaryDesignationRecipient';
import {
  BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE,
  BeneficiaryDesignationsFormShape,
} from './BeneficiaryDesignationsForm.types';
import { BeneficiaryDesignationSuggestionsMatcher } from './BeneficiaryDesignationSuggestionsMatcher';
import { BeneficiaryDesignationTierSectionContextMenu } from './contextMenu/BeneficiaryDesignationTierSectionContextMenu';
import { useToggleBdgResiduaryPaysAllTaxes } from './hooks/useBdgResiduaryPaysAllTaxes';

interface BeneficiaryDesignationTierSectionProps {
  tier: 'primary' | 'contingent';
  entityType: EntityType;
  entityIdOverride?: string;
  isOnHypotheticalWaterfall: boolean;
}

const ADD_BUTTON_LABELS: Record<string, string> = {
  primary: 'Add primary beneficiary',
  contingent: 'Add contingent beneficiary',
};

type BdgDistributionStatus =
  | 'ASSETS_REMAIN'
  | 'NEEDS_RESIDUARY'
  | 'FULLY_DISTRIBUTED'
  | 'EXCEEDS_100_PERCENT';

function getBdgDistributionStatus(
  recipients: DispositiveProvisionsFormRecipient[] | undefined
): BdgDistributionStatus {
  if (!recipients?.length) return 'ASSETS_REMAIN';
  const { isFullyDistributed, exceeds100Percent } =
    calculateResiduaryRecipients(recipients);
  if (exceeds100Percent) return 'EXCEEDS_100_PERCENT';
  if (isFullyDistributed) return 'FULLY_DISTRIBUTED';
  return 'NEEDS_RESIDUARY';
}

export function BeneficiaryDesignationTierSection({
  tier,
  entityType,
  entityIdOverride,
  isOnHypotheticalWaterfall,
}: BeneficiaryDesignationTierSectionProps) {
  const canEditHouseholdData = useCanEditDispositiveProvisions();
  const { control, setValue, setFocus } =
    useFormContext<BeneficiaryDesignationsFormShape>();
  const { disabled: disabledFromContext } = useFormFieldsDisabled();

  // Watches this tier's `residuaryPaysAllTaxes` flag and materializes each
  // recipient's `bearsTax` when it flips (residuary bears tax when on; each
  // recipient defaults from its recipient type when off).
  useToggleBdgResiduaryPaysAllTaxes({ tier });

  const recipientsFieldName =
    `${BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE}.${tier}.recipients` as const;

  const {
    append,
    remove,
    move,
    replace,
    fields: recipients,
  } = useFieldArray({
    control,
    name: recipientsFieldName,
    keyName: '_id',
  });

  /**
   * Tracks the IDs of recipients that should be expanded by default.
   * When new recipients are added, their IDs are stored here
   * so the accordion knows to expand them on mount.
   */
  const [forceExpandIds, setForceExpandIds] = useState<string[]>([]);

  /**
   * Tracks the IDs of recipients that should be collapsed.
   * This is used to ensure that when a new recipient is added,
   * all previous recipients are collapsed.
   */
  const [closedAccordionIds, setClosedAccordionIds] = useState<string[]>([]);

  /**
   * A ref to track if an addition was made to the recipients list.
   * This is used to determine if we should expand the most recently
   * added recipient entries.
   */
  const hasUnhandledAddition = useRef(false);

  /**
   * How many recipients were just added, so the layout effect can expand and
   * scroll to all of them (not just the last) after a bulk split add.
   */
  const pendingNewCount = useRef(0);

  const handleAddRecipient = useCallback(
    (overrides?: Partial<DispositiveProvisionsFormRecipient>) => {
      const existingRecipientIds = recipients.map((r) => r._id);
      setClosedAccordionIds(existingRecipientIds);

      // New beneficiaries default to in-kind transfer (matches the DP form,
      // whose transfer method defaults to IN_KIND), so the tier's "transfer
      // assets in kind" toggle reads as checked by default. `defaultRecipient`
      // (shared with DP V1) seeds LiquidateToCash, so override it here.
      append(
        {
          ...defaultRecipient,
          transferMethod: TransferMethod.InKind,
          ...overrides,
        },
        { shouldFocus: false }
      );
      pendingNewCount.current = 1;
      hasUnhandledAddition.current = true;
    },
    [append, recipients]
  );

  const handleAddResiduary = useCallback(() => {
    handleAddRecipient({
      dispositionKind_ButtonGroup: 'other',
      dispositionKind_Select:
        DispositiveProvisionDispositionKind.AnythingLeftOver,
    });
  }, [handleAddRecipient]);

  const handleBulkAddRecipients = useCallback(
    (newRecipients: DispositiveProvisionsFormRecipient[]) => {
      const existingRecipientIds = recipients.map((r) => r._id);
      setClosedAccordionIds(existingRecipientIds);

      const startIndex = recipients.length;
      append(newRecipients);

      // Mark each new recipient's transferTaxKind as dirty so
      // useDefaultBdgRecipientTransferTaxKind doesn't overwrite the values
      // chosen in the split modal with relationship-based defaults.
      newRecipients.forEach((r, i) => {
        if (r.transferTaxKind) {
          setValue(
            `${recipientsFieldName}.${startIndex + i}.transferTaxKind` as PathsOf<BeneficiaryDesignationsFormShape>,
            r.transferTaxKind as never,
            { shouldDirty: true }
          );
        }
      });

      pendingNewCount.current = newRecipients.length;
      hasUnhandledAddition.current = true;
    },
    [append, recipients, recipientsFieldName, setValue]
  );

  const handleReplaceRecipients = useCallback(
    (newRecipients: DispositiveProvisionsFormRecipient[]) => {
      replace(newRecipients);

      // Mark each replaced recipient's transferTaxKind as dirty so
      // useDefaultBdgRecipientTransferTaxKind doesn't overwrite copied types
      // with relationship-based defaults (empty/replaced tiers have no saved
      // default at those indices, so the hook would otherwise re-default them).
      newRecipients.forEach((r, i) => {
        if (r.transferTaxKind) {
          setValue(
            `${recipientsFieldName}.${i}.transferTaxKind` as PathsOf<BeneficiaryDesignationsFormShape>,
            r.transferTaxKind as never,
            { shouldDirty: true }
          );
        }
      });
    },
    [replace, recipientsFieldName, setValue]
  );

  const handleRemoveRecipient = useCallback(
    (index: number) => {
      // Don't resetField before remove() — resetField clears the dirty flag at
      // this index, and after remove() shifts subsequent items down, the now-
      // shifted recipient inherits the cleared dirty state.
      remove(index);
    },
    [remove]
  );

  const handleReorder = (oldIndex: number, newIndex: number) => {
    move(oldIndex, newIndex);
  };

  useLayoutEffect(() => {
    if (!hasUnhandledAddition.current) return;
    const count = pendingNewCount.current || 1;
    const newRecipients = recipients.slice(-count);
    if (newRecipients.length > 0) {
      // Scroll to the first new recipient so the user sees all additions
      const firstNew = newRecipients[0];
      if (firstNew) {
        const element = document.querySelector(
          `[data-rbd-draggable-id="${firstNew._id}"]`
        );
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
      setForceExpandIds(newRecipients.map((r) => r._id));

      // Focus the first new recipient's input so the user can pick a
      // recipient without clicking into it. The typeahead's menu stays
      // closed on focus (openOnFocus is disabled on the recipient field).
      // Deferred so the accordion has expanded and the input is mounted.
      const firstNewIndex = recipients.length - newRecipients.length;
      const recipientIdPath =
        `${recipientsFieldName}.${firstNewIndex}.recipientId` as PathsOf<BeneficiaryDesignationsFormShape>;
      setTimeout(() => setFocus(recipientIdPath));
    }
    pendingNewCount.current = 0;
    hasUnhandledAddition.current = false;
  }, [recipients, recipientsFieldName, setFocus]);

  const isEmpty = recipients.length === 0;

  // Watch the live recipients values to compute distribution status. The
  // field-array `recipients` only updates on add/remove, not on edits.
  const watchedRecipients = useWatch({
    control,
    name: recipientsFieldName,
  }) as DispositiveProvisionsFormRecipient[] | undefined;

  const status = useMemo(
    () => getBdgDistributionStatus(watchedRecipients),
    [watchedRecipients]
  );

  const statusContent = useMemo(() => {
    switch (status) {
      case 'ASSETS_REMAIN':
        return (
          <Typography variant="body-xs">Undistributed assets remain</Typography>
        );
      case 'NEEDS_RESIDUARY':
        return (
          <Stack direction="row" gap={1} alignItems="center">
            <AlertTriangleFilledIcon
              color={COLORS.FUNCTIONAL.WARNING['600']}
              size={16}
            />
            <Typography
              variant="title-xs"
              sx={{ color: COLORS.FUNCTIONAL.WARNING['600'] }}
            >
              Undistributed assets remain
              {canEditHouseholdData && (
                <>
                  {EMPTY_CONTENT_HYPHEN}
                  <LinkButton
                    color={COLORS.FUNCTIONAL.WARNING['600']}
                    display="add residuary"
                    onClick={handleAddResiduary}
                  />
                </>
              )}
            </Typography>
          </Stack>
        );
      case 'FULLY_DISTRIBUTED':
        return (
          <Stack direction="row" gap={1} alignItems="center">
            <CheckFilledIcon color={COLORS.TEAL['600']} size={16} />
            <Typography variant="title-xs" sx={{ color: COLORS.TEAL['600'] }}>
              Fully distributed
            </Typography>
          </Stack>
        );
      case 'EXCEEDS_100_PERCENT':
        return (
          <Stack direction="row" gap={1} alignItems="center">
            <AlertTriangleFilledIcon
              color={COLORS.FUNCTIONAL.WARNING['600']}
              size={16}
            />
            <Typography
              variant="title-xs"
              sx={{ color: COLORS.FUNCTIONAL.WARNING['600'] }}
            >
              Distributions specified exceed 100%
            </Typography>
          </Stack>
        );
    }
  }, [status, canEditHouseholdData, handleAddResiduary]);

  return (
    <Stack direction="column" spacing={2}>
      <SectionHeader
        statusContent={statusContent}
        addButton={
          canEditHouseholdData ? (
            <Button
              variant="secondary"
              size="xs"
              startIcon={PlusIcon}
              onClick={() => handleAddRecipient()}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {ADD_BUTTON_LABELS[tier]}
            </Button>
          ) : null
        }
        menu={
          <BeneficiaryDesignationTierSectionContextMenu
            tier={tier}
            entityType={entityType}
            entityIdOverride={entityIdOverride}
            isOnHypotheticalWaterfall={isOnHypotheticalWaterfall}
            onBulkAddRecipients={handleBulkAddRecipients}
            onReplaceRecipients={handleReplaceRecipients}
          />
        }
      />

      <BeneficiaryDesignationSuggestionsMatcher tier={tier} />

      {isEmpty ? null : (
        <Stack direction="column" px={2}>
          <AccordionContext.Provider value={{ closedAccordionIds }}>
            <SortableList
              items={recipients}
              getId={(r) => r._id}
              onReorder={handleReorder}
              disabled={disabledFromContext}
            >
              <Box component="ul" sx={{ p: 0, m: 0, listStyleType: 'none' }}>
                {recipients.map((recipient, index) => (
                  <BeneficiaryDesignationRecipient
                    key={recipient._id}
                    recipientId={recipient._id}
                    index={index}
                    tier={tier}
                    onRemove={() => handleRemoveRecipient(index)}
                    forceExpandIds={forceExpandIds}
                    defaultExpandAll={false}
                    disabled={disabledFromContext}
                    entityIdOverride={entityIdOverride}
                    entityType={entityType}
                    isOnHypotheticalWaterfall={isOnHypotheticalWaterfall}
                  />
                ))}
              </Box>
            </SortableList>
          </AccordionContext.Provider>
        </Stack>
      )}
    </Stack>
  );
}

interface SectionHeaderProps {
  statusContent: ReactNode;
  addButton: ReactNode;
  menu: ReactNode;
}

function SectionHeader({ statusContent, addButton, menu }: SectionHeaderProps) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      px={2}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Stack
          padding="10px"
          bgcolor={COLORS.NAVY[100]}
          borderRadius="50%"
          justifyContent="center"
          alignItems="center"
        >
          <Home03Icon
            size={20}
            color={COLORS.NAVY[600]}
            sx={{ display: 'flex' }}
          />
        </Stack>
        {statusContent}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} flexShrink={0}>
        {addButton}
        {menu}
      </Stack>
    </Stack>
  );
}
